import React, { useReducer } from 'react'
import { Button } from '../ui/Button'
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import './QuizEngine.css'

const PHASE = { ANSWERING: 'answering', REVIEW: 'review', RESULT: 'result' }
const PASS_MARK = 4

function quizReducer(state, action) {
  switch (action.type) {
    case 'SELECT':
      if (state.phase !== PHASE.ANSWERING) return state
      return { ...state, selected: { ...state.selected, [action.qIndex]: action.optId } }
    case 'SUBMIT':
      return { ...state, phase: PHASE.REVIEW }
    case 'RETRY':
      return { ...state, selected: {}, phase: PHASE.ANSWERING, attempt: state.attempt + 1 }
    case 'FINISH':
      return { ...state, phase: PHASE.RESULT }
    default:
      return state
  }
}

export function QuizEngine({ quiz, onPass }) {
  const questions = quiz?.questions || []

  const [state, dispatch] = useReducer(quizReducer, {
    selected: {},
    phase: PHASE.ANSWERING,
    attempt: 1,
  })

  if (!questions.length) return null

  const allAnswered = questions.every((_, i) => state.selected[i] !== undefined)

  // Score calculation
  const score = questions.reduce((count, q, i) => {
    const selected = state.selected[i]
    const correct = q.options.find(o => o.correct)
    return selected === correct?.id ? count + 1 : count
  }, 0)

  const passed = score >= PASS_MARK

  function handleSubmit() {
    dispatch({ type: 'SUBMIT' })
  }

  function handleRetry() {
    dispatch({ type: 'RETRY' })
  }

  function handleComplete() {
    onPass && onPass(score)
  }

  // ── Result screen ──
  if (state.phase === PHASE.RESULT || (state.phase === PHASE.REVIEW && passed)) {
    if (state.phase === PHASE.REVIEW) {
      // Show review first, then allow proceeding
    }
  }

  return (
    <div className="quiz" aria-label="Lesson quiz">
      <div className="quiz__header">
        <h2 className="quiz__title">Knowledge check</h2>
        <span className="quiz__meta mono">
          {questions.length} questions · {PASS_MARK}/{questions.length} to pass
        </span>
      </div>

      {/* Questions */}
      <div className="quiz__questions">
        {questions.map((q, qIdx) => {
          const selectedId = state.selected[qIdx]
          const correctOpt = q.options.find(o => o.correct)
          const isReview = state.phase === PHASE.REVIEW
          const isCorrect = isReview && selectedId === correctOpt?.id
          const isWrong = isReview && selectedId && selectedId !== correctOpt?.id
          const explanation = isReview && selectedId
            ? q.options.find(o => o.id === selectedId)?.explanation
            : null
          const correctExplanation = isReview && isWrong
            ? correctOpt?.explanation
            : null

          return (
            <div
              key={q.id || qIdx}
              className={`quiz__question ${isReview ? (isCorrect ? 'quiz__question--correct' : 'quiz__question--wrong') : ''}`}
            >
              <div className="quiz__question-header">
                <span className="quiz__q-num mono">Q{qIdx + 1}</span>
                {isReview && (
                  isCorrect
                    ? <CheckCircle size={16} className="quiz__icon quiz__icon--correct" />
                    : <XCircle size={16} className="quiz__icon quiz__icon--wrong" />
                )}
              </div>
              <p className="quiz__question-text">{q.text}</p>

              <div className="quiz__options" role="radiogroup" aria-label={`Question ${qIdx + 1} options`}>
                {q.options.map(opt => {
                  const isSelected = selectedId === opt.id
                  const isThisCorrect = opt.correct
                  let optClass = 'quiz__option'
                  if (isReview) {
                    if (isThisCorrect) optClass += ' quiz__option--correct'
                    else if (isSelected && !isThisCorrect) optClass += ' quiz__option--wrong'
                    else optClass += ' quiz__option--dim'
                  } else if (isSelected) {
                    optClass += ' quiz__option--selected'
                  }

                  return (
                    <button
                      key={opt.id}
                      className={optClass}
                      onClick={() => !isReview && dispatch({ type: 'SELECT', qIndex: qIdx, optId: opt.id })}
                      disabled={isReview}
                      role="radio"
                      aria-checked={isSelected}
                    >
                      <span className="quiz__opt-letter mono">{opt.id.toUpperCase()}</span>
                      <span className="quiz__opt-text">{opt.text}</span>
                    </button>
                  )
                })}
              </div>

              {isReview && explanation && (
                <div className={`quiz__explanation ${isCorrect ? 'quiz__explanation--correct' : 'quiz__explanation--wrong'}`}>
                  <strong>{isCorrect ? 'Correct.' : 'Incorrect.'}</strong> {explanation}
                  {correctExplanation && (
                    <span className="quiz__correct-note"> The correct answer: {correctExplanation}</span>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Footer actions */}
      <div className="quiz__footer">
        {state.phase === PHASE.ANSWERING && (
          <Button
            onClick={handleSubmit}
            disabled={!allAnswered}
            size="lg"
            fullWidth
          >
            Submit answers
          </Button>
        )}

        {state.phase === PHASE.REVIEW && (
          <div className="quiz__result">
            <div className={`quiz__score ${passed ? 'quiz__score--pass' : 'quiz__score--fail'}`}>
              <span className="quiz__score-num">{score}/{questions.length}</span>
              <span className="quiz__score-label">{passed ? 'Passed ✓' : 'Not yet'}</span>
            </div>

            {passed ? (
              <div className="quiz__pass-actions">
                <p className="quiz__pass-msg">
                  You got {score} out of {questions.length}. Lesson complete.
                </p>
                <Button onClick={handleComplete} size="lg" fullWidth>
                  Mark complete & continue →
                </Button>
              </div>
            ) : (
              <div className="quiz__fail-actions">
                <div className="quiz__fail-msg">
                  <AlertCircle size={16} />
                  <span>You need {PASS_MARK} to pass. Review the explanations above, then try again.</span>
                </div>
                <Button onClick={handleRetry} variant="secondary" size="lg" fullWidth>
                  Try again {state.attempt > 1 ? `(attempt ${state.attempt + 1})` : ''}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
